import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import { FeatureFlag } from '@ps/web-core-enums';
import { matchPath } from 'react-router';

import type { RootState, SelectorCreator, Slicer } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { EventTypeId } from '@swf/sports/types/eventType';

import type { defaultState } from '.';

const selectors = (_slice: Slicer<typeof defaultState>, createSelector: SelectorCreator) => ({
  matchPathnameAlias: () =>
    createSelector(
      (rootState: RootState) => select.cms.getUrlPathname(rootState),
      (rootState: RootState) => select.cms.getAliases(rootState),
      (pathname, aliases) =>
        Object.keys(aliases).find((alias) =>
          matchPath(pathname, {
            path: aliases[alias as keyof typeof aliases],
            exact: true,
            strict: false
          })
        )
    ),
  getUrlParams: () =>
    createSelector(
      (rootState: RootState) => select.cms.matchAlias(rootState),
      (match) => {
        const urlParams = {} as Record<string, string>;
        if (match) {
          const { marketId } = match.params as Record<string, string>;

          Object.assign(urlParams, {
            ...match.params,
            ...(marketId && { marketId: marketId.replace('-', '.') })
          });
        }
        return urlParams;
      }
    ),
  isEventTypeLandingPage() {
    return createSelector(
      (rootState: RootState) => select.feature.check(rootState, FeatureFlag.ISPSportLandingPage),
      (rootState: RootState) => select.sports.getUrlParams(rootState),
      (
        _rootState: RootState,
        {
          eventTypeId,
          configuredLandingPageEventTypeIds
        }: { eventTypeId?: EventTypeId; configuredLandingPageEventTypeIds: EventTypeId[] }
      ) => ({
        eventTypeId,
        configuredLandingPageEventTypeIds
      }),
      (isIspLandingPagesEnabled, params, { eventTypeId, configuredLandingPageEventTypeIds }) =>
        isIspLandingPagesEnabled &&
        configuredLandingPageEventTypeIds.includes(eventTypeId ?? Number(params.eventTypeId))
    );
  },
  isSportsEmbedded() {
    return createSelector(
      (rootState: RootState) => select.cms.getBrandId(rootState) === BrandTypes.BET_STARS,
      (rootState: RootState) => select.device.isEmbedded(rootState),
      (isSports, isEmbedded) => isSports && isEmbedded
    );
  },
  isSports() {
    return createSelector(
      (rootState: RootState) => select.cms.getUrlPathname(rootState),
      (getUrlPathname) => getUrlPathname.includes('/sports/')
    );
  },
  isMobileStickyQuickLinksEnabled() {
    return createSelector(
      (rootState: RootState) => select.device.isPhone(rootState),
      (rootState: RootState) =>
        select.feature.check(rootState, FeatureFlag.ISPEnableMobileStickyQuickLinks),
      (isPhone, isEnableMobileStickyQuickLinks) => isPhone && isEnableMobileStickyQuickLinks
    );
  }
});

export default selectors;
